import { Link } from "react-router-dom";
import { ArrowUpRight, Check, MapPinned } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { services, faq } from "@/data/services";

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};

export default function Services() {
  return (
    <>
      <Seo
        title="Nos services — Négoce, sourcing et approvisionnement B2B | MINES DE PARIS"
        description="Commerce de gros de produits non alimentaires, services interentreprises de négoce, sourcing, approvisionnement et négoce international. Prestations détaillées et FAQ. Devis personnalisé pour chaque service."
        path="/services"
        breadcrumb="Services"
        jsonLd={[faqLd]}
      />
      <PageHero
        crumb="Services"
        eyebrow="Nos services"
        lines={["Négoce, sourcing", "et approvisionnement B2B"]}
        intro="Cinq domaines d'intervention, un déroulé transparent et une proposition personnalisée pour chaque demande. Aucune certification ni référence n'est revendiquée : nous nous engageons sur la méthode et la réactivité."
      />

      <section className="py-16 sm:py-24" data-testid="services-list-section">
        <div className="container-x space-y-6">
          {services.map((s, i) => (
            <Reveal key={s.slug} as="article" id={s.slug} className="card grid scroll-mt-28 gap-8 p-7 sm:p-10 lg:grid-cols-12" data-testid={`service-${s.slug}`}>
              <div className="lg:col-span-5">
                <div className="flex items-center gap-4">
                  <span className="font-heading text-sm font-bold tracking-[0.2em] text-gold">{s.num}</span>
                  <span className="h-px flex-1 bg-line" aria-hidden="true" />
                  <s.icon className="h-7 w-7 text-slate" aria-hidden="true" />
                </div>
                <h2 className="mt-6 font-heading text-2xl font-bold leading-tight text-night sm:text-3xl">{s.title}</h2>
                <p className="mt-4 text-base leading-relaxed text-ink">{s.description}</p>
              </div>
              <div className="lg:col-span-6 lg:col-start-7">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted">Prestations concrètes</h3>
                <ul className="mt-4 space-y-3">
                  {s.prestations.map((p) => (
                    <li key={p} className="flex gap-3 text-sm leading-relaxed text-ink"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" />{p}</li>
                  ))}
                </ul>
                <Link to={`/contact?service=${encodeURIComponent(s.title)}#devis`} className="btn-night mt-8" data-testid={`service-devis-btn-${s.slug}`}>
                  Demander un devis pour ce service <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-mist py-16 sm:py-24" data-testid="perimeter-section">
        <div className="container-x grid gap-10 lg:grid-cols-12 lg:items-center">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Périmètre d'intervention</p>
            <h2 className="h-section mt-5 text-night">France métropolitaine et international</h2>
          </Reveal>
          <Reveal delay={0.1} className="card flex gap-5 p-7 lg:col-span-7">
            <MapPinned className="h-8 w-8 shrink-0 text-gold" aria-hidden="true" />
            <p className="text-base leading-relaxed text-ink">
              Nous intervenons en France métropolitaine ainsi qu'à l'international (import et export). Les modalités — incoterms, transport,
              formalités, délais et conditions de paiement — sont précisées au cas par cas dans le devis, en fonction des produits, des
              volumes et des pays concernés.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24" data-testid="faq-section">
        <div className="container-x grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow">Questions fréquentes</p>
            <h2 className="h-section mt-5 text-night">Vos questions, nos réponses</h2>
            <p className="lede mt-5">Les réponses ci-dessous sont indicatives : seules les conditions figurant dans le devis et nos CGV ont valeur contractuelle.</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <Accordion type="single" collapsible className="divide-y divide-line border-y border-line">
              {faq.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="py-5 text-left font-heading text-base font-bold text-night hover:text-slate hover:no-underline md:text-lg" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="pb-6 text-[15px] leading-relaxed text-ink">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
